"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.switchSelectedHoverStyles = exports.switchFocusStyles = exports.switchHoverStyles = void 0;
exports.Switch = Switch;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const components_1 = require("../components");
const Label_1 = require("../components/Label");
const PresentationContext_1 = require("../components/PresentationContext");
const Css_1 = require("../Css");
const Icon_1 = require("../components/Icon");
const utils_1 = require("../utils");
function Switch(props) {
    const { fieldProps } = (0, PresentationContext_1.usePresentationContext)();
    const { labelLeftFieldWidth = "50%" } = fieldProps !== null && fieldProps !== void 0 ? fieldProps : {};
    const { selected: isSelected, disabled = false, onChange, withIcon, compact = false, label, labelStyle = "inline", hideLabel = false, ...otherProps } = props;
    const isDisabled = !!disabled;
    const ariaProps = { isSelected, isDisabled, ...otherProps };
    const state = (0, utils_1.toToggleState)(isSelected, onChange);
    const ref = (0, react_1.useRef)(null);
    const { inputProps } = (0, react_aria_1.useSwitch)({ ...ariaProps, "aria-label": label }, state, ref);
    const { isFocusVisible: isKeyboardFocus, focusProps } = (0, react_aria_1.useFocusRing)(otherProps);
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)(ariaProps);
    const tooltip = (0, components_1.resolveTooltip)(disabled, props.tooltip);
    const tid = (0, utils_1.useTestIds)(otherProps, label);
    return ((0, jsx_runtime_1.jsxs)("label", { ...hoverProps, css: {
            ...Css_1.Css.relative.cursorPointer.df.wmaxc.usn.$,
            ...(labelStyle === "form" && Css_1.Css.fdc.$),
            ...(labelStyle === "left" && Css_1.Css.w100.aic.jcsb.$),
            ...(labelStyle === "inline" && Css_1.Css.gap2.aic.$),
            ...(labelStyle === "filter" && Css_1.Css.jcsb.gap1.aic.wa.sm.$),
            ...(labelStyle === "centered" && Css_1.Css.fdc.aic.$),
            ...(isDisabled && Css_1.Css.cursorNotAllowed.gray400.$),
        }, children: [labelStyle !== "inline" && labelStyle !== "hidden" && ((0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)(Label_1.Label, { label: label, tooltip: tooltip, xss: Css_1.Css.if(labelStyle === "filter").gray900.$, inline: labelStyle === "left" || labelStyle === "filter" }) })), (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.if(labelStyle === "left").w(labelLeftFieldWidth).$, children: (0, jsx_runtime_1.jsx)("div", { "aria-hidden": "true", css: {
                        ...Css_1.Css.wPx(40).hPx(toggleHeight(compact)).bgGray200.br12.relative.transition.$,
                        ...(isHovered && exports.switchHoverStyles),
                        ...(isKeyboardFocus && exports.switchFocusStyles),
                        ...(isDisabled && Css_1.Css.bgGray300.$),
                        ...(isSelected && Css_1.Css.bgBlue700.$),
                        ...(isSelected && isHovered && exports.switchSelectedHoverStyles),
                    }, children: (0, jsx_runtime_1.jsx)("div", { css: {
                            ...switchCircleDefaultStyles(compact),
                            ...(isDisabled && Css_1.Css.bgGray100.$),
                            ...(isSelected && switchCircleSelectedStyles(compact)),
                        }, children: withIcon && ((0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: isSelected ? "check" : "x", color: isSelected ? Css_1.Palette.Blue700 : Css_1.Palette.Gray400 })) }) }) }), labelStyle === "inline" && ((0, jsx_runtime_1.jsx)(Label_1.Label, { label: label, tooltip: tooltip, inline: true, xss: Css_1.Css.smMd.gray900.if(compact).add("lineHeight", "1").$ })), (0, jsx_runtime_1.jsx)(react_aria_1.VisuallyHidden, { children: (0, jsx_runtime_1.jsx)("input", { ref: ref, ...inputProps, ...focusProps, ...tid }) })] }));
}
/** Styles */
// Element sizes
const toggleHeight = (isCompact) => (isCompact ? 16 : 24);
const circleDiameter = (isCompact) => (isCompact ? 12 : 20);
// Switcher/Toggle element styles
exports.switchHoverStyles = Css_1.Css.bgGray400.$;
exports.switchFocusStyles = Css_1.Css.bshFocus.$;
exports.switchSelectedHoverStyles = Css_1.Css.bgBlue900.$;
// Circle inside Switcher/Toggle element styles
function switchCircleDefaultStyles(isCompact) {
    return {
        ...Css_1.Css.wPx(circleDiameter(isCompact))
            .hPx(circleDiameter(isCompact))
            .br100.bgWhite.bshBasic.absolute.leftPx(2)
            .topPx(2).transition.df.aic.jcc.$,
        svg: Css_1.Css.hPx(toggleHeight(isCompact) / 2).wPx(toggleHeight(isCompact) / 2).$,
    };
}
/**
 * Affecting the `left` property due to transitions only working when there is
 * a previous value to work from.
 *
 * Calculation is as follows:
 * - `100%` is the toggle width
 * - `${circleDiameter(isCompact)}px` is the circle diameter
 * - `2px` is to keep 2px edge spacing.
 */
function switchCircleSelectedStyles(isCompact) {
    return Css_1.Css.left(`calc(100% - ${circleDiameter(isCompact)}px - 2px);`).$;
}
